/**
 * iter_225 · Phase 22.1 — Feature Pulse Soak Audit (Admin only)
 * =============================================================
 * Read-only dashboard showing the real event-based Feature analytics
 * for the 7-day soak window before Phase 5 migration. Sources
 * `/api/admin/feature-pulse/audit`. Never mutates.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (n) => `${((Number(n || 0)) * 100).toFixed(2)}%`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + "Z"; }
  catch { return String(iso); }
};

const StatTile = ({ label, value, sub, tone, testid }) => {
  const toneCls = {
    emerald: "border-emerald-400/40 bg-emerald-500/10 text-emerald-100",
    cyan: "border-cyan-400/40 bg-cyan-500/10 text-cyan-100",
    purple: "border-purple-400/40 bg-purple-500/10 text-purple-100",
    amber: "border-amber-400/40 bg-amber-500/10 text-amber-100",
    rose: "border-rose-400/40 bg-rose-500/10 text-rose-100",
    slate: "border-white/15 bg-white/5 text-white",
  }[tone] || "border-white/15 bg-white/5 text-white";
  return (
    <div data-testid={testid} className={`rounded-xl border p-3 ${toneCls}`}>
      <p className="text-[9px] uppercase tracking-wider font-black opacity-80">{label}</p>
      <p className="text-2xl font-black mt-0.5 leading-none">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-1">{sub}</p>}
    </div>
  );
};

const AdminFeaturePulseAuditPage = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState("");

  const load = useCallback(async (d = days) => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/admin/feature-pulse/audit?days=${d}&top_n=30`);
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Load failed";
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Audit load failed");
    } finally {
      setBusy(false);
    }
  }, [days]);
  useEffect(() => { load(days); }, [load, days]);

  const copyJson = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Copied full audit JSON");
    } catch { toast.error("Copy failed"); }
  };

  const t = data?.totals || {};
  const c = data?.conversions || {};
  const f = data?.filter_stats || {};
  const bs = data?.by_surface || {};
  const meta = data?.meta || {};

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[3px] font-black text-emerald-300">
              📊 Phase 22.1 · Feature Pulse Soak Audit
            </p>
            <h1 className="text-2xl sm:text-3xl font-black mt-1">Real-Analytics Instrumentation Health</h1>
            <p className="text-xs text-gray-400 mt-1 max-w-2xl">
              Read-only. This view is the source of truth for the 7-day soak before Phase 5 retirement of the legacy <code className="text-purple-300">reach × 8</code> synthetic formula. No mutations.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/memberships" className="text-[11px] text-gray-400 hover:text-white underline underline-offset-2">
              ← Admin Membership Center
            </Link>
            {[1, 3, 7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => { setDays(d); load(d); }}
                disabled={busy}
                data-testid={`audit-window-${d}d`}
                className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border ${d === days ? "border-emerald-400/60 bg-emerald-500/20 text-emerald-100" : "border-white/15 bg-white/5 text-gray-300 hover:bg-white/10"}`}
              >
                {d}d
              </button>
            ))}
            <Button onClick={() => load(days)} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-black font-black" data-testid="audit-refresh-btn">
              {busy ? "Loading…" : "Refresh"}
            </Button>
            <Button onClick={copyJson} disabled={!data} className="bg-white/10 hover:bg-white/20" data-testid="audit-copy-json-btn">
              Copy JSON
            </Button>
          </div>
        </div>

        {err && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 text-xs px-3 py-2" data-testid="audit-error">
            {err}
          </div>
        )}

        {!data && !err && (
          <div className="text-xs text-gray-500 py-8">Loading audit…</div>
        )}

        {data && (
          <>
            {/* Meta */}
            <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="audit-meta">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                <div>
                  <p className="text-gray-500 uppercase font-black">Real analytics available since</p>
                  <p className="font-mono text-emerald-200" data-testid="audit-meta-since">
                    {fmtDate(meta.instrumentation_started_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase font-black">Window</p>
                  <p className="font-mono text-white">{meta.window_days} days</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase font-black">Window start</p>
                  <p className="font-mono text-gray-300">{fmtDate(meta.window_start)}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase font-black">TTL retention</p>
                  <p className="font-mono text-gray-300">{meta.retention_days} days</p>
                </div>
              </div>
              <p className="text-[10px] text-amber-200 mt-3 bg-amber-500/5 border border-amber-500/20 rounded p-2">
                <strong>Spotlight attribution:</strong> {meta.spotlight_attribution_note}
              </p>
            </section>

            {/* Totals */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="audit-totals">
              <StatTile
                testid="audit-total-impressions"
                label="Real Feature Impressions"
                value={fmt(t.real_impressions)}
                sub={`across ${fmt(t.unique_submissions)} submissions`}
                tone="emerald"
              />
              <StatTile
                testid="audit-total-clicks"
                label="Feature Clicks (lifetime)"
                value={fmt(t.feature_clicks_lifetime)}
                sub={`CTR ${pct(c.ctr_window)} (window)`}
                tone="cyan"
              />
              <StatTile
                testid="audit-total-feature-plays"
                label="Feature Plays (attributed)"
                value={fmt(t.feature_plays_lifetime)}
                sub={`Play conv ${pct(c.feature_play_conversion_window)}`}
                tone="amber"
              />
              <StatTile
                testid="audit-total-canonical-plays"
                label="Real Plays (top tracks lifetime)"
                value={fmt(t.real_plays_lifetime_top_tracks)}
                sub="canonical play_count · unchanged"
                tone="purple"
              />
            </section>

            {/* Auth + Surface split */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="audit-auth-split">
                <h2 className="text-xs font-black uppercase tracking-wider text-gray-300 mb-2">Authenticated vs Anonymous</h2>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded border border-white/10 bg-black/40 p-2">
                    <p className="text-gray-500 uppercase font-black">Authenticated</p>
                    <p className="text-white font-black text-lg">{fmt(t.authenticated_impressions)}</p>
                  </div>
                  <div className="rounded border border-white/10 bg-black/40 p-2">
                    <p className="text-gray-500 uppercase font-black">Anonymous</p>
                    <p className="text-white font-black text-lg">{fmt(t.anonymous_impressions)}</p>
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="audit-by-surface">
                <h2 className="text-xs font-black uppercase tracking-wider text-gray-300 mb-2">Impressions by Surface</h2>
                <div className="space-y-1 text-[11px]">
                  {Object.entries(bs).length === 0 && (
                    <p className="text-gray-500">No surfaces yet.</p>
                  )}
                  {Object.entries(bs).map(([surface, n]) => (
                    <div key={surface} className="flex justify-between items-center rounded border border-white/10 bg-black/40 p-2">
                      <span className="text-gray-300 font-mono">{surface}</span>
                      <span className="text-white font-black">{fmt(n)}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Filter/Dedup health */}
            <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="audit-filter-stats">
              <h2 className="text-xs font-black uppercase tracking-wider text-gray-300 mb-2">Filtering / Dedup Health</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                <div className="rounded border border-white/10 bg-black/40 p-2" data-testid="audit-dup-rejected">
                  <p className="text-gray-500 uppercase font-black">Duplicates rejected</p>
                  <p className="text-white font-black text-lg">{fmt(f.duplicates_rejected)}</p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2" data-testid="audit-owner-rejected">
                  <p className="text-gray-500 uppercase font-black">Owner self-views excluded</p>
                  <p className="text-white font-black text-lg">{fmt(f.owner_self_views_rejected)}</p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2" data-testid="audit-internal-rejected">
                  <p className="text-gray-500 uppercase font-black">Internal/Admin excluded</p>
                  <p className="text-white font-black text-lg">{fmt(f.internal_rejected)}</p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2" data-testid="audit-bot-rejected">
                  <p className="text-gray-500 uppercase font-black">Bots excluded</p>
                  <p className="text-white font-black text-lg">{fmt(f.bot_rejected)}</p>
                </div>
              </div>
            </section>

            {/* Top submissions table */}
            <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="audit-top-submissions">
              <h2 className="text-xs font-black uppercase tracking-wider text-gray-300 mb-2">
                Top submissions in window (event-based)
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-gray-500 uppercase tracking-wider">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-1.5 pr-2">Track</th>
                      <th className="text-right px-2">Status</th>
                      <th className="text-right px-2">Real imp.</th>
                      <th className="text-right px-2">Clicks</th>
                      <th className="text-right px-2">Feat. plays</th>
                      <th className="text-right px-2">Real plays</th>
                      <th className="text-right px-2">CTR</th>
                      <th className="text-right pl-2">Legacy est.</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {(data.top_submissions || []).map((r) => (
                      <tr key={r.submission_id} className="border-b border-white/5">
                        <td className="py-1 pr-2 truncate max-w-[200px]">
                          <Link to={`/track/${r.submission_id}`} className="text-emerald-300 hover:underline">
                            {r.track_title}
                          </Link>
                          {r.artist_name && <span className="text-gray-500 ml-1">· {r.artist_name}</span>}
                        </td>
                        <td className="text-right px-2 text-cyan-200">{r.status || "—"}</td>
                        <td className="text-right px-2 text-emerald-200">{fmt(r.real_impressions_window)}</td>
                        <td className="text-right px-2 text-cyan-200">{fmt(r.feature_clicks_total)}</td>
                        <td className="text-right px-2 text-amber-200">{fmt(r.feature_plays_total)}</td>
                        <td className="text-right px-2 text-purple-200">{fmt(r.real_plays_total)}</td>
                        <td className="text-right px-2 text-white">{pct(r.ctr)}</td>
                        <td className="text-right pl-2 text-gray-500 line-through">{fmt(r.legacy_impressions_estimate)}</td>
                      </tr>
                    ))}
                    {(data.top_submissions || []).length === 0 && (
                      <tr><td colSpan={8} className="text-center py-6 text-gray-500">No impressions recorded yet in this window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-[9px] text-gray-500 mt-2">
                "Legacy est." = `(boost_applied + floor_applied) × 8`. This is the synthetic number currently rendered by the six remaining consumers. Shown strikethrough because we are NOT presenting it as audience.
              </p>
            </section>

            {/* Migration readiness */}
            <section className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4" data-testid="audit-readiness">
              <h2 className="text-xs font-black uppercase tracking-wider text-emerald-200 mb-2">
                Phase 5 Migration Readiness
              </h2>
              <ul className="text-[11px] text-gray-300 list-disc pl-4 space-y-1">
                <li>Instrumentation ON since <span className="font-mono text-emerald-200">{fmtDate(meta.instrumentation_started_at)}</span></li>
                <li><strong>DO NOT</strong> flip Phase 5 (Profile totals / Feature Recaps / metric helpers / admin defaults / delete `IMPRESSION_MULTIPLIER`) until this soak has been reviewed and explicitly approved.</li>
                <li>Historical <em>Featured before Phase 22</em> tracks will have zero real impressions until they receive fresh views — that is CORRECT, not a bug.</li>
                <li>Report back after 7 days: total real impressions, dedup/filter health, and the delta between real and legacy synthetic numbers.</li>
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFeaturePulseAuditPage;
